import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import {
  EVENT_TYPE_LABELS,
  formatActivityEvent,
  sortSnapshots,
  type ActivityEvent,
  type AuditEventType,
  type SubjectKind,
  type VersionSnapshot
} from '../models/versionHistory';

export type VersionHistoryStatus = 'loading' | 'ready' | 'error';

/** Which modal is open in the History drawer. */
export type HistoryModalKind = 'none' | 'view' | 'diff' | 'restore';

/** A rendered Activity row — the model event plus its formatted line. */
export interface ActivityLine {
  id: string;
  eventType: AuditEventType;
  at: string;
  text: string;
}

interface SnapshotRow {
  id: string;
  subject_kind: SubjectKind;
  subject_id: string;
  version_number: number;
  title: string;
  body: string;
  snapshot_at: string;
  snapshot_by: string | null;
  source_version_id: string | null;
}

interface EventRow {
  id: string;
  subject_kind: SubjectKind;
  subject_id: string;
  event_type: AuditEventType;
  payload: Record<string, unknown> | null;
  actor_id: string | null;
  at: string;
}

interface ProfileRow {
  id: string;
  display_name: string | null;
  username: string | null;
}

function toSnapshot(row: SnapshotRow): VersionSnapshot {
  return {
    id: row.id,
    subjectKind: row.subject_kind,
    subjectId: row.subject_id,
    versionNumber: row.version_number,
    title: row.title,
    body: row.body,
    snapshotAt: row.snapshot_at,
    snapshotBy: row.snapshot_by,
    sourceVersionId: row.source_version_id
  };
}

function toEvent(row: EventRow): ActivityEvent {
  return {
    id: row.id,
    subjectKind: row.subject_kind,
    subjectId: row.subject_id,
    eventType: row.event_type,
    payload: row.payload,
    actorId: row.actor_id,
    at: row.at
  };
}

export interface UseVersionHistoryVM {
  status: VersionHistoryStatus;
  /** Snapshots, newest version first. */
  snapshots: VersionSnapshot[];
  /** Formatted Activity lines, newest first. */
  activityLines: ActivityLine[];
  /** Resolved author display names, keyed by user id. */
  authorHandles: Record<string, string>;
  /** Which modal is open. */
  modal: HistoryModalKind;
  /** The snapshot the open modal targets, or null. */
  modalSnapshot: VersionSnapshot | null;
  /** True while confirmRestore's RPC is in flight. */
  isRestoring: boolean;
  /** Restore error message, or null. */
  restoreError: string | null;
  /** Current Activity event-type filter ('all' or a specific type). */
  activityFilter: string;
  /** Activity lines after the filter is applied. */
  visibleActivityLines: ActivityLine[];
  /** Distinct event-type filter options present in the feed. */
  activityFilterOptions: { value: string; label: string }[];
  /** Resolves a snapshot author's display name. */
  authorFor: (snapshot: VersionSnapshot) => string;
  actions: {
    reload: () => void;
    openView: (versionId: string) => void;
    openDiff: (versionId: string) => void;
    openRestore: (versionId: string) => void;
    closeModal: () => void;
    /** Commits the restore via restore_version_snapshot. Returns true on success. */
    confirmRestore: () => Promise<boolean>;
    setActivityFilter: (next: string) => void;
  };
}

const RESTORE_ERROR = "Couldn't restore that version. Try again.";

/**
 * ViewModel for the polymorphic History drawer. Loads version_snapshots +
 * audit_events for one subject (a document or a work item), resolves author
 * handles, owns the View/Diff/Restore modal lifecycle, and routes Restore
 * through the restore_version_snapshot RPC. This is the single place restore
 * logic lives — DocumentEditor and WorkItemDetail both consume it.
 */
export function useVersionHistoryVM(
  subjectKind: SubjectKind,
  subjectId: string | undefined
): UseVersionHistoryVM {
  const [status, setStatus] = useState<VersionHistoryStatus>('loading');
  const [snapshots, setSnapshots] = useState<VersionSnapshot[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [authorHandles, setAuthorHandles] = useState<Record<string, string>>(
    {}
  );
  const [modal, setModal] = useState<HistoryModalKind>('none');
  const [modalVersionId, setModalVersionId] = useState<string | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [activityFilter, setActivityFilterState] = useState<string>('all');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!subjectId) return;
    let cancelled = false;
    setStatus('loading');
    void (async () => {
      const [snapRes, evtRes] = await Promise.all([
        supabase
          .from('version_snapshots')
          .select(
            'id, subject_kind, subject_id, version_number, title, body, snapshot_at, snapshot_by, source_version_id'
          )
          .eq('subject_kind', subjectKind)
          .eq('subject_id', subjectId)
          .order('version_number', { ascending: false }),
        supabase
          .from('audit_events')
          .select(
            'id, subject_kind, subject_id, event_type, payload, actor_id, at'
          )
          .eq('subject_kind', subjectKind)
          .eq('subject_id', subjectId)
          .order('at', { ascending: false })
      ]);
      if (cancelled) return;
      if (snapRes.error || evtRes.error) {
        setStatus('error');
        return;
      }
      const snapRows = (snapRes.data ?? []) as ReadonlyArray<SnapshotRow>;
      const evtRows = (evtRes.data ?? []) as ReadonlyArray<EventRow>;
      const nextSnapshots = snapRows.map(toSnapshot);
      const nextEvents = evtRows.map(toEvent);

      const ids = Array.from(
        new Set(
          [
            ...nextSnapshots.map((s) => s.snapshotBy),
            ...nextEvents.map((e) => e.actorId)
          ].filter((v): v is string => !!v)
        )
      );
      const handleMap: Record<string, string> = {};
      if (ids.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', ids);
        if (cancelled) return;
        for (const p of (profileRows ?? []) as ReadonlyArray<ProfileRow>) {
          handleMap[p.id] = p.display_name || p.username || 'Someone';
        }
      }

      setSnapshots(sortSnapshots(nextSnapshots));
      setEvents(nextEvents);
      setAuthorHandles(handleMap);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [subjectKind, subjectId, reloadTick]);

  const reload = useCallback(() => setReloadTick((n) => n + 1), []);

  const authorFor = useCallback(
    (snapshot: VersionSnapshot) =>
      snapshot.snapshotBy
        ? authorHandles[snapshot.snapshotBy] ?? 'Someone'
        : 'Someone',
    [authorHandles]
  );

  const activityLines = useMemo<ActivityLine[]>(
    () =>
      events.map((e) => ({
        id: e.id,
        eventType: e.eventType,
        at: e.at,
        text: formatActivityEvent(
          e,
          e.actorId ? authorHandles[e.actorId] ?? null : null
        )
      })),
    [events, authorHandles]
  );

  const visibleActivityLines = useMemo(
    () =>
      activityFilter === 'all'
        ? activityLines
        : activityLines.filter((l) => l.eventType === activityFilter),
    [activityLines, activityFilter]
  );

  const activityFilterOptions = useMemo(() => {
    const seen = new Set<AuditEventType>();
    events.forEach((e) => seen.add(e.eventType));
    return [
      { value: 'all', label: 'All event types' },
      ...Array.from(seen).map((t) => ({
        value: t,
        label: EVENT_TYPE_LABELS[t]
      }))
    ];
  }, [events]);

  const modalSnapshot = useMemo(
    () => snapshots.find((s) => s.id === modalVersionId) ?? null,
    [snapshots, modalVersionId]
  );

  const openView = useCallback((versionId: string) => {
    setRestoreError(null);
    setModalVersionId(versionId);
    setModal('view');
  }, []);

  const openDiff = useCallback((versionId: string) => {
    setRestoreError(null);
    setModalVersionId(versionId);
    setModal('diff');
  }, []);

  const openRestore = useCallback((versionId: string) => {
    setRestoreError(null);
    setModalVersionId(versionId);
    setModal('restore');
  }, []);

  const closeModal = useCallback(() => {
    setModal('none');
    setModalVersionId(null);
    setRestoreError(null);
  }, []);

  const confirmRestore = useCallback(async (): Promise<boolean> => {
    if (!modalVersionId) return false;
    setRestoreError(null);
    setIsRestoring(true);
    const { data, error } = await supabase.rpc('restore_version_snapshot', {
      p_version_id: modalVersionId
    });
    setIsRestoring(false);
    if (error) {
      setRestoreError(RESTORE_ERROR);
      return false;
    }
    const envelope = data as
      | { ok: true; snapshot_id: string; version_number: number }
      | { ok: false; error: string }
      | null;
    // Benign no-op: version title/body already matches live state — not an error.
    if (envelope && !envelope.ok && envelope.error === 'already_at_version') {
      closeModal();
      return true;
    }
    if (!envelope || envelope.ok !== true) {
      setRestoreError(RESTORE_ERROR);
      return false;
    }
    setModal('none');
    setModalVersionId(null);
    reload();
    return true;
  }, [modalVersionId, reload, closeModal]);

  const setActivityFilter = useCallback((next: string) => {
    setActivityFilterState(next);
  }, []);

  return {
    status,
    snapshots,
    activityLines,
    authorHandles,
    modal,
    modalSnapshot,
    isRestoring,
    restoreError,
    activityFilter,
    visibleActivityLines,
    activityFilterOptions,
    authorFor,
    actions: {
      reload,
      openView,
      openDiff,
      openRestore,
      closeModal,
      confirmRestore,
      setActivityFilter
    }
  };
}
